import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { WorkbenchConfiguratorItem } from '../../configurator/applications/workbench-configurator-item';
import { WorkbenchService } from '../../../../services/configurators-2d/applications/workbench.service';
import { Material } from 'src/app/models/configurators-2d/applications/workbench/materials/material';
import { Subscription } from 'rxjs';
import {
  ConfigurationPlan,
  isPlanColorValid,
  isPlanDimensionsValid,
} from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { Configurators2DComponent } from '../../configurators-2d.component';
import { IWorkbenchPreview } from '../preview/i-workbench-preview';

@Component({
  selector: 'ng-workbench-materials-colors',
  templateUrl: './materials-colors.component.html',
})
export class WorkbenchMaterialsColorsComponent extends WorkbenchConfiguratorItem implements OnInit, OnDestroy {
  // Evenement qui emet si on a choisi du quartz pour étape crédence
  @Output() isSplashbackNeeded: EventEmitter<boolean> = new EventEmitter<boolean>();

  // Groupe visible en cours de configuration
  displayedGroupIndex: number;
  // Plan visible en cours de configuration
  displayedPlanIndex: number;
  //Sous menu visible
  subStep: number = 1;

  // Matériaux, épaisseurs et couleurs disponibles.
  materials: Material[];
  // Plans disponibles pour la configuration (non présent dans un groupe)
  availablePlans: ConfigurationPlan[];
  subscriptions: Subscription[] = [];

  preview: IWorkbenchPreview;

  constructor(
    public workbenchService: WorkbenchService,
    configurators2DComponent: Configurators2DComponent
  ) {
    super(workbenchService);
    this.preview = configurators2DComponent.preview as IWorkbenchPreview;
  }

  ngOnInit(): void {
    // Légende
    this.preview.hideLegend();
    // shapes sélectionnés
    let shapeIds: number[] = this.configuration.workbenchConfigurationPlans
      .map(cp => cp.shape.id)
      .filter((shapeId, i, arr) => arr.findIndex(s => s === shapeId) === i);

    this.service.getMaterialsColors(shapeIds).then(materials => {
      this.materials = materials;
      this.getAvailablePlans();
    });

    this.subscriptions.push(
      this.preview.onPlanSelect.subscribe(id => {
        const group = this.configuration.workbenchConfigurationGroups?.find(g =>
          g.workbenchConfigurationPlans.some(p => p.displayOrder === id)
        );

        this.displayedGroupIndex = group != null ? group.displayOrder : 0;
        this.displayedPlanIndex = id;

        if (group != null) {
          this.subStep = 1;
        } else {
          this.subStep = 2;
        }
      })
    );

    this.subscriptions.push(
      this.preview.onGroupCreate.subscribe(plansIds => {
        setTimeout(() => {
          this.workbenchService.createOrUpdateConfigurationGroup(plansIds);
          this.getAvailablePlans();
        });
      })
    );

    this.subscriptions.push(
      this.preview.onGroupDelete.subscribe(plansIds => {
        setTimeout(() => {
          this.workbenchService.updateOrDeleteConfigurationGroup(plansIds);
          this.getAvailablePlans();
        });
      })
    );

    this.preview.previewSelectPlanById(1);

    this.preview.actionByConfiguratorStep();

    // Si des plans existent avec une couleur on affecte la couleur.
    if (this.configuration.workbenchConfigurationPlans.length > 0) {
      this.configuration.workbenchConfigurationPlans.forEach(plan => {
        if (plan.color != null) {
          this.preview.loadImage(plan.color.picture.path, plan.displayOrder);
        }
      });
    }

    this.preview.previewSelectPlanById(this.displayedPlanIndex);
  }

  /**
   * Vérifie si on a des groupes de plans pour afficher la section.
   * @returns
   */
  isGroupMaterialConfigurationActive() {
    return (
      this.materials != null &&
      this.configuration.workbenchConfigurationGroups != undefined &&
      this.configuration.workbenchConfigurationGroups?.length > 0
    );
  }

  /**
   * Vérifie si on a des plans de disponibles pour afficher la section
   * @returns
   */
  isPlanMaterialConfigurationActive() {
    return this.materials != null && this.availablePlans?.length > 0;
  }

  /**
   * Récupère si des plans sont dispos hors des groupes
   * @returns
   */
  getAvailablePlans() {
    if (this.isGroupMaterialConfigurationActive()) {
      this.availablePlans = this.configuration.workbenchConfigurationPlans.filter(plan => !plan.isInGroup);
    } else {
      this.availablePlans = this.configuration.workbenchConfigurationPlans;
    }
    this.availablePlans.sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Fonction d'affichage des sous menus
   * @param subStep sous étape
   */
  async toggleCollapse(subStep: number) {
    this.subStep = subStep;
  }

  /**
   * Affiche le groupe en train d'être traité.
   * @param isVisible
   * @param index
   */
  toggleCollapseGroup(isVisible: boolean, index: number) {
    if (isVisible) {
      this.displayedPlanIndex = 0;
      this.displayedGroupIndex = index;

      const group = this.configuration.workbenchConfigurationGroups.find(g => g.displayOrder === index);
      if (group && group.workbenchConfigurationPlans.length > 0) {
        // Select the first plan of the group to trigger highlight in preview
        const firstPlanId = group.workbenchConfigurationPlans[0].displayOrder;
        this.preview.previewSelectPlanById(firstPlanId);
      }

    } else {
      this.displayedGroupIndex = 0;
      // Optionally deselect in preview?
      // this.preview.previewSelectPlanById(null); // If supported
    }
  }

  /**
   * Affiche le plan en train d'être traité.
   * @param isVisible
   * @param index
   */
  toggleCollapsePlan(isVisible: boolean, index: number) {
    if (isVisible) {
      this.displayedGroupIndex = 0;
      this.displayedPlanIndex = index;
    } else {
      this.displayedPlanIndex = 0;
    }
    this.preview.previewSelectPlanById(index);
  }

  isNextButtonActive(): boolean {
    return this.configuration.workbenchConfigurationPlans.every(plan => isPlanColorValid(plan));
  }

  checkPageChangeCapability(): boolean {
    let groupsExistAndAreValids = true;
    if (
      this.configuration.workbenchConfigurationGroups?.length != undefined &&
      this.configuration.workbenchConfigurationGroups?.length > 0
    ) {
      groupsExistAndAreValids = this.configuration.workbenchConfigurationGroups.every(group => group.isValid());
    }
    return (
      this.configuration.workbenchConfigurationPlans?.length > 0 &&
      this.configuration.workbenchConfigurationPlans.every(plan => isPlanDimensionsValid(plan)) &&
      groupsExistAndAreValids
    );
  }

  checkIfSplashbackIsNeeded() {
    if (
      this.configuration.workbenchConfigurationGroups?.some(g =>
        g.workbenchConfigurationPlans.some(p => p.material.canHaveSplashback)
      ) ||
      this.configuration.workbenchConfigurationPlans?.some(p => p.material.canHaveSplashback)
    ) {
      this.isSplashbackNeeded.emit(true);
    } else {
      this.isSplashbackNeeded.emit(false);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
